import { FC, useMemo } from 'react';
import {
  SizedCell,
  SortableHeadCell,
  TextEllipsisWithTooltip,
} from '__components/Kizen/Table';
import {
  getLink,
  getRelationshipLabel,
} from '__components/Timeline2/EventBlocks/helpers';
import { usePreReleaseFeatures } from 'hooks/usePreReleaseFeatures';
import { useTranslation } from 'react-i18next';
import DateTimeInputInline from '__components/Inputs/inline/DateTimeInput';
import { snakeToCamelCaseKeys } from 'services/helpers';
import { SMART_FIELD_TYPES } from '../../../../constants';
import { SmartConnectorHistory } from '__pages/SmartConnectors/types';
import ActionCell from '__components/Tables/ActionCell';

const ControlsCell: FC<
  any & {
    onSelectAction: (action: 'view', data: SmartConnectorHistory) => void;
  }
> = ({ onSelectAction, ...props }) => {
  const { t } = useTranslation();
  const options = useMemo(
    () => [
      {
        label: t('View Details'),
        value: 'view',
      },
    ],
    [t]
  );
  return (
    <ActionCell
      title={t('Select Action')}
      menuType="absolute"
      options={options}
      onSelectAction={onSelectAction}
      {...props}
      width="15px"
      padding="15px"
    />
  );
};

export const useColumns = (
  onSelectAction: (
    { value }: { value: 'view' },
    data: SmartConnectorHistory
  ) => void
) => {
  const { t } = useTranslation();
  const preReleaseFeatures = usePreReleaseFeatures();
  return useMemo(
    () => [
      {
        id: 'spacer-start',
        headCell: <SizedCell width="15px" />,
        cell: <SizedCell width="15px" />,
        format: () => <span />,
      },
      {
        id: 'team_member',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Team Member')}
            width="200px"
            padding="60px"
          />
        ),
        cell: <SizedCell width="200px" padding="60px" />,
        format: (_: any, data: SmartConnectorHistory) => {
          return (
            <TextEllipsisWithTooltip
              {...(preReleaseFeatures
                ? {
                    type: 'anchor',
                    href: getLink(data.initiator, {
                      type: SMART_FIELD_TYPES.team_selector,
                      metaInfo: null,
                    }),
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  }
                : {})}
            >
              {getRelationshipLabel(null, snakeToCamelCaseKeys(data.initiator))}
            </TextEllipsisWithTooltip>
          );
        },
      },
      {
        id: 'date_modified',
        headCell: (
          <SortableHeadCell
            by="created"
            numeric
            label={t('Modified Date')}
            width="150px"
            padding="60px"
          />
        ),
        cell: <SizedCell width="150px" padding="60px" />,
        format: (_, data: SmartConnectorHistory) => {
          return data?.initiator?.type === 'employee' ? (
            <DateTimeInputInline
              readOnly
              value={data.created}
              showDateTooltip
            />
          ) : null;
        },
      },
      {
        id: 'changes',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Change(s) Made')}
            minWidth="120px"
            padding="60px"
          />
        ),
        cell: <SizedCell minWidth="120px" padding="60px" />,
        format: (_, data: SmartConnectorHistory) => {
          return (
            <TextEllipsisWithTooltip>
              {data.type_display_name}
            </TextEllipsisWithTooltip>
          );
        },
      },
      {
        id: 'actions',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={' '}
            width="15px"
            padding="15px"
          />
        ),
        cell: <ControlsCell onSelectAction={onSelectAction} />,
      },
    ],
    [preReleaseFeatures, onSelectAction, t]
  );
};
